import * as React from "react";
import { styled, alpha } from "@mui/material/styles";
import {
  Box,
  Toolbar,
  IconButton,
  Typography,
  InputBase,
  Badge,
  MenuItem,
  Menu,
  List,
  CssBaseline,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  ListSubheader,
} from "@mui/material";
import Cookies from "js-cookie";
import { useNavigate, useLocation } from "react-router-dom";
import { Theme, CSSObject } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import { Tooltip } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
// ICONS
import LabIcon from "@mui/icons-material/Science";
import SearchIcon from "@mui/icons-material/Search";
import AccountCircle from "@mui/icons-material/AccountCircle";
import NotificationsIcon from "@mui/icons-material/Notifications";
import MoreIcon from "@mui/icons-material/MoreVert";
import EventIcon from "@mui/icons-material/Event";
import PeopleIcon from "@mui/icons-material/People";
import AssessmentIcon from "@mui/icons-material/Assessment";
import PaymentIcon from "@mui/icons-material/Payment";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import ArticleIcon from "@mui/icons-material/Article";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import BiotechIcon from "@mui/icons-material/Biotech";
import InventoryIcon from "@mui/icons-material/Inventory";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import LogoutIcon from "@mui/icons-material/Logout";
import DashboardIcon from "@mui/icons-material/Dashboard";
import { getUserRole, getLabName } from "../../../utils/roleGuard";
import waryLogo from "../../../assets/img/wary-logo.png";
import "./HeaderComponent.scss";

const drawerWidth = 240;

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(3),
    width: "auto",
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
    [theme.breakpoints.up("md")]: {
      width: "20ch",
    },
  },
}));

export default function HeaderComponent() {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] =
    React.useState<null | HTMLElement>(null);

  const isMenuOpen = Boolean(anchorEl);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);
  const navigate = useNavigate();
  const location = useLocation();

  const { notifications } = useSelector((state: RootState) => state.notifications);
  const unreadCount = notifications?.filter((n: any) => n.status === "pending")?.length || 0;

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    handleMobileMenuClose();
  };

  const handleLogout = () => {
    Cookies.remove("token");
    navigate("/signin");
    handleMenuClose();
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  const isActive = (path: string) => {
    const currentPath = location.pathname;
    if (path === ".") {
      return currentPath === "/dashboard" || currentPath === "/dashboard/";
    }
    const resolvedPath = `/dashboard/${path.replace("./", "")}`;
    return currentPath.startsWith(resolvedPath);
  };

  const menuId = "primary-search-account-menu";
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      id={menuId}
      keepMounted
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      open={isMenuOpen}
      onClose={handleMenuClose}
      sx={{ mt: 1 }}
    >
      <MenuItem onClick={() => { handleMenuClose(); navigate('/dashboard/profile'); }} sx={{ gap: 1 }}>
        <AccountCircle fontSize="small" /> Profile
      </MenuItem>
      <Divider />
      <MenuItem onClick={handleLogout} sx={{ gap: 1, color: "error.main" }}>
        <LogoutIcon fontSize="small" /> Log out
      </MenuItem>
    </Menu>
  );

  const mobileMenuId = "primary-search-account-menu-mobile";
  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      <MenuItem onClick={() => { handleMobileMenuClose(); navigate("/dashboard/notifications"); }}>
        <IconButton size="large" color="inherit">
          <Badge badgeContent={unreadCount} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>
        <p>Notifications</p>
      </MenuItem>
      <MenuItem onClick={handleProfileMenuOpen}>
        <IconButton size="large" color="inherit">
          <AccountCircle />
        </IconButton>
        <p>Profile</p>
      </MenuItem>
    </Menu>
  );

  const labName = getLabName();

  const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
  });

  const DrawerHeader = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
  }));

  const AppBar = styled(MuiAppBar)(({ theme }) => ({
    zIndex: theme.zIndex.drawer + 1,
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
  }));

  const Drawer = styled(MuiDrawer)(({ theme }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }));

  const userRole = getUserRole();
  const isLabUser = userRole === "labUser" || userRole === "labAdmin";

  // Role-based navigation: Lab users see Reports first; Admins see Customers first
  const mainMenuItems = isLabUser
    ? [
        { text: "Dashboard", icon: <DashboardIcon />, path: "." },
        // Phase 2: Upcoming Tests — lab scheduling hidden for Phase 1
        // { text: "Upcoming Tests", icon: <LabIcon />, path: "./upcoming-tests" },
        // Phase 2: Appointments — hidden for Phase 1
        // { text: "Appointments", icon: <EventIcon />, path: "./appointments" },
        { text: "Customers", icon: <PersonSearchIcon />, path: "./customers" },
        { text: "Reports", icon: <AssessmentIcon />, path: "./reports" },
        { text: "OCR Review", icon: <DocumentScannerIcon />, path: "./ocr" },
        { text: "Health Scores", icon: <MonitorHeartIcon />, path: "./health-scores" },
        // Phase 2: Lab Members — hidden for Phase 1
        // { text: "Lab Members", icon: <PeopleIcon />, path: "./lab-members" },
      ]
    : [
        { text: "Dashboard", icon: <DashboardIcon />, path: "." },
        { text: "Customers", icon: <PersonSearchIcon />, path: "./customers" },
        // Phase 2: Appointments — hidden for Phase 1
        // { text: "Appointments", icon: <EventIcon />, path: "./appointments" },
        { text: "Reports", icon: <AssessmentIcon />, path: "./reports" },
        { text: "OCR Review", icon: <DocumentScannerIcon />, path: "./ocr" },
        { text: "Health Scores", icon: <MonitorHeartIcon />, path: "./health-scores" },
        // Phase 2: Upcoming Tests — lab scheduling hidden for Phase 1
        // { text: "Upcoming Tests", icon: <LabIcon />, path: "./upcoming-tests" },
        // Phase 2: Lab Members — hidden for Phase 1
        // { text: "Lab Members", icon: <PeopleIcon />, path: "./lab-members" },
      ];

  const catalogMenuItems = [
    // Phase 2: Tests — lab test catalog hidden for Phase 1
    // { text: "Tests", icon: <BiotechIcon />, path: "./tests" },
    // Phase 2: Packages — health checkup packages hidden for Phase 1
    // { text: "Packages", icon: <InventoryIcon />, path: "./checkups" },
    { text: "Content", icon: <ArticleIcon />, path: "./content" },
    { text: "Offers", icon: <LocalOfferIcon />, path: "./offers" },
  ];

  const managementMenuItems = [
    { text: "Payments", icon: <PaymentIcon />, path: "./payments" },
    { text: "Subscriptions", icon: <CardMembershipIcon />, path: "./subscriptions" },
    { text: "Notifications", icon: <NotificationsIcon />, path: "./notifications" },
  ];

  const adminMenuItems = [
    { text: "Analytics", icon: <AnalyticsIcon />, path: "./analytics" },
    { text: "User Management", icon: <AdminPanelSettingsIcon />, path: "./users" },
  ];

  const renderMenuSection = (
    items: { text: string; icon: React.ReactNode; path: string }[],
    sectionLabel?: string
  ) => (
    <List
      dense
      subheader={
        sectionLabel ? (
          <ListSubheader
            component="div"
            sx={{
              fontSize: "0.7rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              color: "text.disabled",
              lineHeight: "2rem",
              px: 2.5,
              bgcolor: "transparent",
            }}
          >
            {sectionLabel}
          </ListSubheader>
        ) : undefined
      }
    >
      {items.map((item) => {
        const active = isActive(item.path);
        return (
          <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
            <ListItemButton
              sx={[
                {
                  minHeight: 44,
                  px: 2.5,
                  borderRadius: "0 24px 24px 0",
                  mx: 0.5,
                  mr: 1,
                  mb: 0.25,
                  transition: "all 0.2s",
                  justifyContent: "initial",
                  ...(active && {
                    backgroundColor: "primary.main",
                    color: "#fff",
                    "&:hover": { backgroundColor: "primary.dark" },
                    "& .MuiListItemIcon-root": { color: "#fff" },
                  }),
                },
              ]}
              onClick={() => handleNavigation(item.path)}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  justifyContent: "center",
                  color: active ? "#fff" : "text.secondary",
                  mr: 2,
                }}
              >
                <Tooltip title={item.text} arrow placement="right">
                  {item.icon as React.ReactElement}
                </Tooltip>
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: "0.82rem",
                  fontWeight: active ? 600 : 400,
                }}
              />
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );

  const renderSideMenu = (
    <Drawer variant="permanent">
      <DrawerHeader sx={{ justifyContent: "center", px: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            component="img"
            src={waryLogo}
            alt="wary"
            sx={{ height: 28, width: "auto", objectFit: "contain" }}
          />
          <Typography variant="caption" sx={{ color: "text.secondary", letterSpacing: "0.04em", lineHeight: 1.2 }}>
            | healthcare
          </Typography>
        </Box>
      </DrawerHeader>
      <Divider />
      {renderMenuSection(mainMenuItems, "Main")}
      <Divider sx={{ mx: 1 }} />
      {renderMenuSection(catalogMenuItems, "Catalog")}
      <Divider sx={{ mx: 1 }} />
      {renderMenuSection(managementMenuItems, "Management")}
      {userRole === "superAdmin" && (
        <>
          <Divider sx={{ mx: 1 }} />
          {renderMenuSection(adminMenuItems, "Admin")}
        </>
      )}
    </Drawer>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      <CssBaseline />
      {renderSideMenu}
      <AppBar position="fixed">
        <Toolbar>
          {labName && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <LabIcon sx={{ fontSize: "1.3rem" }} />
              <Typography
                variant="h6"
                noWrap
                component="div"
                sx={{ fontWeight: 600, fontSize: "1rem" }}
              >
                {labName}
              </Typography>
            </Box>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 0.5 }}>
            <Search>
              <SearchIconWrapper>
                <SearchIcon />
              </SearchIconWrapper>
              <StyledInputBase placeholder="Search…" inputProps={{ "aria-label": "search" }} />
            </Search>
            <Tooltip title="Notifications">
              <IconButton color="inherit" onClick={() => navigate("/dashboard/notifications")}>
                <Badge badgeContent={unreadCount} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>
            <Tooltip title="Account">
              <IconButton
                size="large"
                edge="end"
                aria-controls={menuId}
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
                color="inherit"
              >
                <AccountCircle />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ display: { xs: "flex", md: "none" } }}>
            <IconButton
              size="large"
              edge="end"
              aria-controls={mobileMenuId}
              aria-haspopup="true"
              onClick={handleMobileMenuOpen}
              color="inherit"
            >
              <MoreIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      {renderMobileMenu}
      {renderMenu}
    </Box>
  );
}
